// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ProjectReportType,
  getProjectReport,
} from '@/report/api/get-project-report';
import { useReportInterval } from '@/report/hooks/use-report-interval';
import { useQuery } from '@tanstack/react-query';

interface Options {
  type: ProjectReportType;
  start: string;
  end: string;
}

/**
 * A React hook that manages the state of a request for a project report for the
 * current user's organization.
 *
 * @param options.type - The type of the report.
 * @param options.start - The start of the time period. If not provided, the
 * start date is 31 days before the current date.
 * @param options.end - The end of the time period. If not provided, the end
 * date is the day before the current date.
 */
export function useProjectReport(options: Options) {
  const interval = useReportInterval(options);

  const query = useQuery({
    refetchOnWindowFocus: false,
    queryKey: ['report', options.type, interval.toString()],
    queryFn: () => {
      if (options.type === 'upcoming') {
        return getProjectReport(options.type, { start: options.start });
      }

      if (options.type === 'past') {
        return getProjectReport(options.type, { end: options.end });
      }

      return getProjectReport(options.type, {
        start: interval.start.toISODate(),
        end: interval.end.toISODate(),
      });
    },
  });

  return { interval, ...query };
}
